import { useCallback, useEffect, useState } from "react";
import api, { apiError, localApi } from "@/lib/api";
import { PageHeader, Loading, EmptyState, ConfirmDialog } from "@/components/common";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, CircleNotch, HandCoins, Money, Eye, Prohibit, FilePdf } from "@phosphor-icons/react";

const inputCls = "rounded-sm border-input bg-background text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";
const btnPrimary = "rounded-sm bg-primary text-primary-foreground hover:bg-primary/80";
const btnOutline = "rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground";

const FREQUENCIES = [
  { value: "daily", label: "Diaria" },
  { value: "weekly", label: "Semanal" },
  { value: "biweekly", label: "Quincenal" },
  { value: "monthly", label: "Mensual" },
];
const freqLabel = (v) => FREQUENCIES.find((f) => f.value === v)?.label || v;

const STATUS = {
  active: { label: "Activo", cls: "border-emerald-500/40 text-emerald-500" },
  paid: { label: "Pagado", cls: "border-sky-500/40 text-sky-500" },
  cancelled: { label: "Cancelado", cls: "border-border text-muted-foreground" },
};

const EMPTY = { employee_id: "", principal: "", interest_pct: "", installment: "", frequency: "biweekly", start_date: "", notes: "" };

export default function Prestamos() {
  const [loans, setLoans] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", note: "" });
  const [historyTarget, setHistoryTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [filter, setFilter] = useState("active");
  const [empFilter, setEmpFilter] = useState("all");

  const load = useCallback(() => {
    Promise.all([localApi.get("/loans"), api.get("/employees")])
      .then(([l, e]) => {
        setLoans(Array.isArray(l.data) ? l.data : []);
        setEmployees((Array.isArray(e.data) ? e.data : []).filter((x) => x.active));
      })
      .catch((err) => {
        toast.error(apiError(err));
        setLoans([]);
      });
  }, []);

  useEffect(load, [load]);

  const total = Number(form.principal || 0) * (1 + Number(form.interest_pct || 0) / 100);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const emp = employees.find((x) => x.id === form.employee_id);
    try {
      await localApi.post("/loans", {
        ...form,
        employee_name: emp?.full_name || "",
        employee_code: emp?.employee_code || "",
        principal: Number(form.principal) || 0,
        interest_pct: Number(form.interest_pct) || 0,
        installment: Number(form.installment) || 0,
      });
      toast.success("Préstamo registrado");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await localApi.post(`/loans/${payTarget.id}/pay`, { amount: Number(payForm.amount) || 0, note: payForm.note });
      toast.success(`Abono aplicado: ${fmtMoney(data.applied)}`);
      setPayTarget(null);
      setPayForm({ amount: "", note: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const cancelLoan = async () => {
    try {
      await localApi.patch(`/loans/${cancelTarget.id}`, { status: "cancelled" });
      toast.success("Préstamo cancelado");
      setCancelTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const downloadPdf = async (l) => {
    try {
      const res = await localApi.get(`/loans/${l.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `prestamo_${l.employee_code || l.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err, "No se pudo descargar el estado de cuenta"));
    }
  };

  if (!loans) return <Loading label="Cargando préstamos..." />;

  const filtered = loans.filter((l) => (filter === "all" || l.status === filter) && (empFilter === "all" || l.employee_id === empFilter));
  const activeDebt = loans.filter((l) => l.status === "active").reduce((s, l) => s + (l.remaining || 0), 0);
  const empSel = employees.find((x) => x.id === empFilter);
  const empDebt = empSel ? loans.filter((l) => l.employee_id === empFilter && l.status === "active").reduce((s, l) => s + (l.remaining || 0), 0) : 0;
  const empActiveCount = empSel ? loans.filter((l) => l.employee_id === empFilter && l.status === "active").length : 0;

  return (
    <div data-testid="prestamos-page">
      <PageHeader title="Préstamos" description={`Préstamos a empleados con rebajo automático en planilla · Por cobrar: ${fmtMoney(activeDebt)}`} testid="prestamos-header">
        <Button className={btnPrimary} data-testid="new-loan-button" onClick={() => { setForm(EMPTY); setOpen(true); }}>
          <Plus className="mr-2" size={15} /> Nuevo préstamo
        </Button>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex flex-wrap gap-2">
          {[["active", "Activos"], ["paid", "Pagados"], ["cancelled", "Cancelados"], ["all", "Todos"]].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`loan-filter-${k}`}
              className={`px-3 py-1.5 text-xs rounded-sm border transition-colors duration-200 ${filter === k ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground hover:bg-accent/60"}`}>
              {label}
            </button>
          ))}
        </div>
        <Select value={empFilter} onValueChange={setEmpFilter}>
          <SelectTrigger className={`${inputCls} w-64`} data-testid="loan-employee-filter"><SelectValue placeholder="Todos los empleados" /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">Todos los empleados</SelectItem>
            {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name} · {e.employee_code}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {empSel && (
        <p className="text-sm text-muted-foreground mb-4" data-testid="loan-employee-summary">
          <span className="text-foreground font-medium">{empSel.full_name}</span>: {empActiveCount} préstamo(s) activo(s) · deuda pendiente <span className="text-foreground font-medium">{fmtMoney(empDebt)}</span>
        </p>
      )}

      {filtered.length === 0 ? (
        <EmptyState title="Sin préstamos" description="Registra el primer préstamo; la cuota se rebajará automáticamente en la planilla según la frecuencia elegida." />
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto bg-card">
          <table className="w-full text-sm min-w-[980px]" data-testid="loans-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="p-3 font-normal">Empleado</th>
                <th className="p-3 font-normal">Monto</th>
                <th className="p-3 font-normal">Interés</th>
                <th className="p-3 font-normal">Total a pagar</th>
                <th className="p-3 font-normal">Cuota</th>
                <th className="p-3 font-normal">Progreso</th>
                <th className="p-3 font-normal">Estado</th>
                <th className="p-3 font-normal text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((l) => {
                const st = STATUS[l.status] || STATUS.active;
                const pct = l.total > 0 ? Math.min(100, Math.round(((l.paid_amount || 0) / l.total) * 100)) : 0;
                return (
                  <tr key={l.id} className="hover:bg-muted/70 transition-colors duration-200" data-testid={`loan-row-${l.id}`}>
                    <td className="p-3">
                      <p className="text-foreground font-medium">{l.employee_name}</p>
                      <p className="text-xs text-muted-foreground">{l.employee_code} · {fmtDate(l.start_date || l.created_at)}</p>
                    </td>
                    <td className="p-3 text-foreground/80">{fmtMoney(l.principal)}</td>
                    <td className="p-3 text-foreground/80">{l.interest_pct ? `${l.interest_pct}%` : "—"}</td>
                    <td className="p-3 text-foreground font-medium">{fmtMoney(l.total)}</td>
                    <td className="p-3 text-foreground/80">{fmtMoney(l.installment)} <span className="text-xs text-muted-foreground">{freqLabel(l.frequency)}</span></td>
                    <td className="p-3">
                      <div className="w-28">
                        <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
                          <span>{fmtMoney(l.paid_amount || 0)}</span>
                          <span>{pct}%</span>
                        </div>
                        <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
                          <div className="h-full bg-primary transition-all duration-300" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">Resta {fmtMoney(l.remaining)}</p>
                      </div>
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className={`rounded-sm font-normal ${st.cls}`} data-testid={`loan-status-${l.id}`}>{st.label}</Badge>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent" title="Estado de cuenta PDF" data-testid={`loan-pdf-${l.id}`} onClick={() => downloadPdf(l)}>
                          <FilePdf size={15} />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent" title="Historial de abonos" data-testid={`loan-history-${l.id}`} onClick={() => setHistoryTarget(l)}>
                          <Eye size={15} />
                        </Button>
                        {l.status === "active" && (
                          <>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-500 hover:text-emerald-400 hover:bg-emerald-950/30" title="Registrar abono" data-testid={`loan-pay-${l.id}`} onClick={() => { setPayTarget(l); setPayForm({ amount: "", note: "" }); }}>
                              <Money size={15} />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-400 hover:bg-red-950/30" title="Cancelar préstamo" data-testid={`loan-cancel-${l.id}`} onClick={() => setCancelTarget(l)}>
                              <Prohibit size={15} />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground flex items-center gap-2"><HandCoins size={18} className="text-primary" /> Nuevo préstamo</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4" data-testid="loan-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Empleado *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger className={inputCls} data-testid="loan-employee-select"><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name} · {e.employee_code}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Monto prestado (L) *</Label>
                <Input type="number" step="0.01" min="0" required className={inputCls} data-testid="loan-principal" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Interés % (opcional)</Label>
                <Input type="number" step="0.01" min="0" className={inputCls} placeholder="0" data-testid="loan-interest" value={form.interest_pct} onChange={(e) => setForm({ ...form, interest_pct: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Se rebajará (L) *</Label>
                <Input type="number" step="0.01" min="0" required className={inputCls} data-testid="loan-installment" value={form.installment} onChange={(e) => setForm({ ...form, installment: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Frecuencia del rebajo</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger className={inputCls} data-testid="loan-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Fecha de inicio</Label>
              <Input type="date" className={inputCls} data-testid="loan-start-date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            {Number(form.principal) > 0 && (
              <div className="border border-border rounded-sm p-3 text-sm flex justify-between" data-testid="loan-total-preview">
                <span className="text-muted-foreground">Total a pagar{Number(form.interest_pct) > 0 ? ` (${form.principal} + ${form.interest_pct}%)` : ""}</span>
                <span className="text-foreground font-bold">{fmtMoney(total)}</span>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              La cuota se descuenta automáticamente al generar la planilla: frecuencia quincenal descuenta en planillas quincenales, mensual en mensuales, semanal en semanales, y diaria multiplica la cuota por los días trabajados del período.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notas</Label>
              <Textarea className={`${inputCls} min-h-[60px]`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving || !form.employee_id} className={btnPrimary} data-testid="loan-save-button">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Registrar préstamo
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payTarget} onOpenChange={() => setPayTarget(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Registrar abono</DialogTitle>
          </DialogHeader>
          {payTarget && (
            <form onSubmit={pay} className="space-y-4" data-testid="loan-pay-form">
              <p className="text-sm text-muted-foreground">
                {payTarget.employee_name} · resta <span className="text-foreground font-medium">{fmtMoney(payTarget.remaining)}</span> de {fmtMoney(payTarget.total)}
              </p>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Monto del abono (L) *</Label>
                <Input type="number" step="0.01" min="0" max={payTarget.remaining} required autoFocus className={inputCls} data-testid="loan-pay-amount" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Nota</Label>
                <Input className={inputCls} placeholder="Ej. Abono en efectivo" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} className={btnPrimary} data-testid="loan-pay-confirm">
                  {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Aplicar abono
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyTarget} onOpenChange={() => setHistoryTarget(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Historial de abonos</DialogTitle>
          </DialogHeader>
          {historyTarget && (
            <div data-testid="loan-history-dialog">
              <p className="text-sm text-muted-foreground mb-3">{historyTarget.employee_name} · {fmtMoney(historyTarget.total)}</p>
              {(historyTarget.payments || []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">Sin abonos registrados aún.</p>
              ) : (
                <div className="border border-border rounded-sm divide-y divide-border max-h-64 overflow-y-auto">
                  {historyTarget.payments.map((pay) => (
                    <div key={pay.id} className="flex items-center justify-between p-3 text-sm">
                      <div>
                        <p className="text-foreground">{fmtMoney(pay.amount)}</p>
                        <p className="text-[11px] text-muted-foreground">{pay.note || "Abono"}{pay.payroll_id ? " · vía planilla" : ""}</p>
                      </div>
                      <span className="text-xs text-muted-foreground">{fmtDate(pay.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!cancelTarget} onOpenChange={() => setCancelTarget(null)}
        title="Cancelar préstamo"
        description={`Se cancelará el préstamo de ${cancelTarget?.employee_name} (${fmtMoney(cancelTarget?.remaining)} pendientes). Ya no se rebajará en planillas.`}
        onConfirm={cancelLoan} confirmLabel="Cancelar préstamo" testid="cancel-loan" />
    </div>
  );
}
